import { EXERCISE_CATEGORY, EXERCISE_CATEGORY_LABELS, type TrainingExerciseDto } from '@ukinfo/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { AGE_CLASSES, exerciseMeta } from '../../lib/training';
import { Button, EmptyState, ErrorBlock, Input, LoadingBlock, Select } from '../ui';
import { CategoryBadge, SourceBadges } from './common';
import { PitchDiagram } from './PitchDiagram';

/**
 * Suche und Filter ueber die Uebungsbibliothek - gemeinsam fuer die Seite
 * "Uebungen" und die Auswahl beim Planen einer Einheit.
 */

export interface ExerciseFilter {
  search: string;
  category: string;
  focus: string;
  age: string;
  players: string;
  source: 'ALL' | 'CATALOG' | 'OWN';
}

export const EMPTY_FILTER: ExerciseFilter = { search: '', category: '', focus: '', age: '', players: '', source: 'ALL' };

export function isFiltered(filter: ExerciseFilter): boolean {
  return JSON.stringify(filter) !== JSON.stringify(EMPTY_FILTER);
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Die Liste zum Filter; die Suche wartet kurz, bis das Tippen pausiert. */
export function useExerciseList(filter: ExerciseFilter, enabled = true) {
  const search = useDebounced(filter.search.trim(), 250);
  const query = { ...filter, search };
  return useQuery({
    queryKey: ['training-exercises', query],
    queryFn: () => api.get<TrainingExerciseDto[]>('/api/training/exercises', query),
    enabled,
    // Beim Tippen bleibt die bisherige Liste stehen, bis die neue da ist.
    placeholderData: keepPreviousData,
  });
}

/** Schwerpunkte, die es in der ungefilterten Bibliothek gibt (Grundlage der Auswahl). */
export function useFocusOptions(enabled = true): string[] {
  const all = useExerciseList(EMPTY_FILTER, enabled);
  return useMemo(() => {
    const seen = new Map<string, string>();
    for (const exercise of all.data ?? []) {
      for (const tag of exercise.focus) if (!seen.has(tag.toLocaleLowerCase('de'))) seen.set(tag.toLocaleLowerCase('de'), tag);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b, 'de'));
  }, [all.data]);
}

export function ExerciseFilterBar({
  filter,
  onChange,
  focusOptions,
}: {
  filter: ExerciseFilter;
  onChange: (filter: ExerciseFilter) => void;
  focusOptions: string[];
}): JSX.Element {
  const set = (patch: Partial<ExerciseFilter>): void => onChange({ ...filter, ...patch });
  return (
    <div className="space-y-2">
      <Input
        type="search"
        aria-label="Übungen suchen"
        placeholder="Titel, Schwerpunkt oder Material suchen …"
        value={filter.search}
        onChange={(event) => set({ search: event.target.value })}
      />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <Select aria-label="Kategorie" className="text-sm" value={filter.category} onChange={(event) => set({ category: event.target.value })}>
          <option value="">Kategorie</option>
          {EXERCISE_CATEGORY.map((category) => (
            <option key={category} value={category}>{EXERCISE_CATEGORY_LABELS[category]}</option>
          ))}
        </Select>
        <Select aria-label="Schwerpunkt" className="text-sm" value={filter.focus} onChange={(event) => set({ focus: event.target.value })}>
          <option value="">Schwerpunkt</option>
          {focusOptions.map((tag) => (
            <option key={tag} value={tag}>{tag}</option>
          ))}
        </Select>
        <Select aria-label="Altersklasse" className="text-sm" value={filter.age} onChange={(event) => set({ age: event.target.value })}>
          <option value="">Altersklasse</option>
          {AGE_CLASSES.map((age) => (
            <option key={age} value={age}>U{age}</option>
          ))}
        </Select>
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          max={50}
          aria-label="Spielerzahl"
          placeholder="Spielerzahl"
          value={filter.players}
          onChange={(event) => set({ players: event.target.value })}
        />
        <Select aria-label="Quelle" className="text-sm" value={filter.source} onChange={(event) => set({ source: event.target.value as ExerciseFilter['source'] })}>
          <option value="ALL">Alle Quellen</option>
          <option value="CATALOG">Standard</option>
          <option value="OWN">Eigene</option>
        </Select>
      </div>
      {isFiltered(filter) && (
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(EMPTY_FILTER)}>
          Filter zurücksetzen
        </Button>
      )}
    </div>
  );
}

/** Kleine Skizze oder Platzhalter, damit die Karten gleich aufgebaut sind. */
function Thumbnail({ exercise }: { exercise: TrainingExerciseDto }): JSX.Element {
  return (
    <div className="w-24 shrink-0 sm:w-28">
      {exercise.diagram ? (
        <PitchDiagram diagram={exercise.diagram} className="max-h-28 rounded-lg" />
      ) : (
        <div className="flex aspect-[4/3] items-center justify-center rounded-lg bg-slate-100 text-[11px] text-slate-400" aria-hidden="true">
          {exercise.isLink ? 'extern' : 'ohne Skizze'}
        </div>
      )}
    </div>
  );
}

/** Eine Uebung in der Kartenliste; Klick fuehrt in die Detailansicht. */
export function ExerciseCard({ exercise }: { exercise: TrainingExerciseDto }): JSX.Element {
  const meta = exerciseMeta(exercise);
  return (
    <li>
      <Link
        to={`/sport/uebungen/${exercise.id}`}
        className="flex h-full gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50/40"
      >
        <Thumbnail exercise={exercise} />
        <span className="min-w-0 flex-1">
          <span className="block break-words font-semibold text-slate-900">{exercise.title}</span>
          <span className="mt-1 flex flex-wrap gap-1">
            <SourceBadges exercise={exercise} />
            <CategoryBadge category={exercise.category} />
          </span>
          {meta.length > 0 && <span className="mt-1 block text-xs text-slate-500">{meta.join(' · ')}</span>}
          {exercise.focus.length > 0 && <span className="mt-0.5 block break-words text-xs text-slate-500">{exercise.focus.join(', ')}</span>}
        </span>
      </Link>
    </li>
  );
}

/** Zeile in der Auswahl beim Planen: Titel, Merkmale und ein Knopf zum Hinzufuegen. */
export function ExercisePickRow({ exercise, onPick }: { exercise: TrainingExerciseDto; onPick: (exercise: TrainingExerciseDto) => void }): JSX.Element {
  const meta = exerciseMeta(exercise);
  return (
    <li className="flex items-center gap-3 py-2.5">
      <div className="w-16 shrink-0 sm:w-20">
        {exercise.diagram ? (
          <PitchDiagram diagram={exercise.diagram} className="max-h-20 rounded-md" />
        ) : (
          <div className="flex aspect-[4/3] items-center justify-center rounded-md bg-slate-100 text-[10px] text-slate-400" aria-hidden="true">
            –
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm font-medium text-slate-900">{exercise.title}</p>
        <div className="mt-0.5 flex flex-wrap gap-1">
          <SourceBadges exercise={exercise} />
          <CategoryBadge category={exercise.category} />
        </div>
        {meta.length > 0 && <p className="mt-0.5 text-xs text-slate-500">{meta.join(' · ')}</p>}
      </div>
      <Button type="button" size="sm" variant="secondary" aria-label={`${exercise.title} hinzufügen`} onClick={() => onPick(exercise)}>
        Hinzufügen
      </Button>
    </li>
  );
}

/** Ladezustand, Fehler und leeres Ergebnis fuer eine Listenabfrage. */
export function ExerciseListState({ query, empty }: { query: ReturnType<typeof useExerciseList>; empty?: string }): JSX.Element | null {
  if (query.isLoading) return <LoadingBlock />;
  if (query.error) return <ErrorBlock error={query.error} />;
  if (query.data && query.data.length === 0) {
    return <EmptyState title="Keine Übungen gefunden" description={empty ?? 'Ändere die Suche oder die Filter.'} />;
  }
  return null;
}
